import { useState } from 'react';
import { BookOpen, Sparkles, UserCheck, Library, TrendingUp, Video, FileText, Download } from 'lucide-react';
import { useGetUsers } from '../../services/query/user/user';
import { useGetEducationContents, useGetEducationMetrics } from '../../services/query/education/education';
import { useDebounce } from '../../hooks/useDebounce';
import { EducationalContent, AssignmentConfig, ContentType, mapApiToEducationalContent } from './educationTypes';
import PatientSpecificTab from './PatientSpecificTab';
import GeneralEducationTab from './GeneralEducationTab';
import CRMEngagementTab from './CRMEngagementTab';
import AIContentGeneratorModal from './AIContentGeneratorModal';
import AssignmentModal from './AssignmentModal';

type MainTabType = 'patient-specific' | 'general' | 'crm';

function getTypeIcon(type: ContentType): JSX.Element {
  switch (type) {
    case 'video': return <Video className="w-5 h-5" />;
    case 'article': return <FileText className="w-5 h-5" />;
    case 'infographic': return <BookOpen className="w-5 h-5" />;
    case 'pdf': return <Download className="w-5 h-5" />;
  }
}

const mainTabs = [
  { id: 'patient-specific' as MainTabType, label: 'Patient-Specific Care', icon: UserCheck },
  { id: 'general' as MainTabType, label: 'General Education', icon: Library },
  { id: 'crm' as MainTabType, label: 'CRM & Engagement', icon: TrendingUp },
];

export default function EducationModule() {
  const [activeTab, setActiveTab] = useState<MainTabType>('patient-specific');
  // const [searchTerm, setSearchTerm] = useState('');
  const [contentSearch, setContentSearch] = useState('');
  const [
    debouncedSearch, 
    // setDebouncedSearch
  ] = useDebounce<string | undefined>(undefined);

  const { 
    data: usersData, 
    // isLoading: patientsLoading 
  } = useGetUsers({
    name: debouncedSearch,
    page: 1,
    limit: 100,
  });
  const patients = usersData?.data ?? [];

  const { data: contentsData, isLoading: contentsLoading } = useGetEducationContents({
    page: 1,
    limit: 12,
    search: contentSearch || undefined,
  });
  const educationContent: EducationalContent[] = (contentsData?.data ?? []).map(mapApiToEducationalContent);

  const { data: metricsData, isLoading: metricsLoading } = useGetEducationMetrics();

  // const [selectedPatient, setSelectedPatient] = useState<string | null>(null);
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [selectedContent, setSelectedContent] = useState<EducationalContent | null>(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentConfig, setAssignmentConfig] = useState<AssignmentConfig>({
    contentId: '',
    targetType: 'all',
    targetCriteria: [],
    scheduleType: 'ai-optimized',
    selectedPatients: [],
  });

  const handleOpenAssignmentModal = (content: EducationalContent) => {
    setSelectedContent(content);
    setAssignmentConfig({
      contentId: content.id,
      targetType: 'all',
      targetCriteria: [],
      scheduleType: 'ai-optimized',
      selectedPatients: [],
    });
    setShowAssignmentModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <BookOpen className="w-7 h-7 mr-3 text-green-600" />
              Patient Education Center
            </h1>
            <p className="text-gray-600 mt-1">
              AI-powered educational content delivery and engagement tracking
            </p>
          </div>
          <button
            onClick={() => setShowAIGenerator(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-md"
          >
            <Sparkles className="w-5 h-5" />
            <span>Generate AI Content</span>
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8">
            {mainTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {activeTab === 'patient-specific' && (
        <PatientSpecificTab
          metricsData={metricsData}
          metricsLoading={metricsLoading}
        />
      )}

      {activeTab === 'general' && (
        <GeneralEducationTab
          content={educationContent}
          isLoading={contentsLoading}
          searchTerm={contentSearch}
          onSearchChange={setContentSearch}
          onAssign={handleOpenAssignmentModal}
        />
      )}

      {activeTab === 'crm' && (
        <CRMEngagementTab content={educationContent} />
      )}

      {showAIGenerator && (
        <AIContentGeneratorModal onClose={() => setShowAIGenerator(false)} />
      )}

      {showAssignmentModal && selectedContent && (
        <AssignmentModal
          content={selectedContent}
          patients={patients}
          assignmentConfig={assignmentConfig}
          onConfigChange={setAssignmentConfig}
          getTypeIcon={getTypeIcon}
          onClose={() => setShowAssignmentModal(false)}
        />
      )}
    </div>
  );
}
